'use client';

import { useMemo, type ReactNode } from 'react';

import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';

import { UserCheck } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { Alert, AlertDescription } from '@ci/ui/alert';
import { If } from '@ci/ui/if';
import { Trans } from '@ci/ui/trans';

import { useLastAuthMethod } from '../hooks/use-last-auth-method';

interface ExistingAccountHintProps {
  signInPath?: string;
  className?: string;
}

// we force dynamic import to avoid hydration errors
export const ExistingAccountHint = dynamic(
  async () => ({ default: ExistingAccountHintImpl }),
  {
    ssr: false,
  },
);

export function ExistingAccountHintImpl({
  signInPath = '/auth/sign-in',
  className,
}: ExistingAccountHintProps) {
  const { hasLastMethod, methodType, providerName, isOAuth } =
    useLastAuthMethod();

  const params = useSearchParams();
  const t = useTranslations();

  const isInvite = params.get('invite_token');

  if (isInvite) {
    signInPath = signInPath + '?invite_token=' + isInvite;
  }

  // Get the appropriate method description for the hint
  // This must be called before any conditional returns to follow Rules of Hooks
  const methodLabel = useMemo(() => {
    if (isOAuth && providerName) {
      return providerName;
    }

    switch (methodType) {
      case 'password':
        return t('auth.methodPassword');
      case 'otp':
        return t('auth.methodOtp');
      case 'magic_link':
        return t('auth.methodMagicLink');
      default:
        return t('auth.methodDefault');
    }
  }, [methodType, isOAuth, providerName, t]);

  // Don't show anything until loaded or if no last method
  if (!hasLastMethod) {
    return null;
  }

  return (
    <If condition={Boolean(methodLabel)}>
      <Alert data-test={'existing-account-hint'} className={className}>
        <UserCheck className="h-4 w-4" />

        <AlertDescription className={'text-xs'}>
          <Trans
            i18nKey="auth.existingAccountHint"
            values={{ method: methodLabel }}
            components={{
              highlight: (chunks: ReactNode) => (
                <span className="font-medium">{chunks}</span>
              ),
              signInLink: (chunks: ReactNode) => (
                <Link href={signInPath} className="font-medium underline">
                  {chunks}
                </Link>
              ),
            }}
          />
        </AlertDescription>
      </Alert>
    </If>
  );
}
